import React from 'react'
import T from 'prop-types'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import FavoriteIcon from '@mui/icons-material/Favorite'

import ChoiceRecap from 'components/atoms/ChoiceRecap'

import {
  FILTER_CAPS_FIRST_CHOICE,
  FILTER_CAPS_NONE_CHOICE,
  FILTER_CAPS_ALL_CHOICES,
  FILTER_CAPS_NO_CHOICES,
  FILTER_CAPS_BLIND_POSITIVE_OFFERS,
  FILTER_CAPS_BLIND_NEGATIVE_OFFERS,
} from 'hooks/useCarsFilters'

import { getBlindOfferCapsByType } from './blindOffersLib'

const VARIANT_LIST = 'list'
const VARIANT_DETAILS = 'details'
const BLIND_TYPE_POSITIVE = 'positive'
const BLIND_TYPE_NEGATIVE = 'negative'

const toNumericValue = (value) => {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : null
}

const clampRecapCount = (count, carsCount) =>
  Number.isFinite(count)
    ? Math.min(Math.max(count, 0), Math.max(carsCount, 0))
    : 0

const toNonNegativeNumericValue = (value) => {
  const parsedValue = toNumericValue(value)
  if (parsedValue === null) {
    return null
  }
  return Math.max(parsedValue, 0)
}

export default function ChoicesRecap({
  variant = VARIANT_LIST,
  cars = [],
  caps,
  blindOffersUsedByType,
  blindOffersTotalByType,
  showBlindOffers = false,
  showBlindPositiveOffers = true,
  filteringCap,
  setFilteringCaps,
  onlyFavorites = false,
  onToggleFavorites,
  readOnly = false,
}) {
  const { t } = useTranslation()

  const numberFormatter = new Intl.NumberFormat('it-IT')

  const isDetails = variant === VARIANT_DETAILS

  const hasBlindPrice = (car) =>
    car?.blindPrice !== null &&
    car?.blindPrice !== undefined &&
    car?.blindPrice !== ''

  const hasBlindPositivePrice = (car) => {
    if (!hasBlindPrice(car)) {
      return false
    }

    if (car?.blindType === BLIND_TYPE_POSITIVE) {
      return true
    }
    if (car?.blindType === BLIND_TYPE_NEGATIVE) {
      return false
    }

    const blindPrice = toNumericValue(car?.blindPrice)
    const groupPrice = toNumericValue(car?.price)

    return blindPrice !== null && groupPrice !== null && blindPrice > groupPrice
  }

  const hasBlindNegativePrice = (car) => {
    if (!hasBlindPrice(car)) {
      return false
    }

    if (car?.blindType === BLIND_TYPE_NEGATIVE) {
      return true
    }
    if (car?.blindType === BLIND_TYPE_POSITIVE) {
      return false
    }

    const blindPrice = toNumericValue(car?.blindPrice)
    const groupPrice = toNumericValue(car?.price)

    return blindPrice !== null && groupPrice !== null && blindPrice < groupPrice
  }

  const hasBlindPositiveSignal = (car) => {
    if (hasBlindPositivePrice(car)) {
      return true
    }

    const positiveCount = toNonNegativeNumericValue(
      car?.blindPositiveOfferCount
    )
    if (positiveCount !== null) {
      return positiveCount > 0
    }

    return car?.hasBlindPositiveOffer === true
  }

  const hasBlindNegativeSignal = (car) => {
    if (hasBlindNegativePrice(car)) {
      return true
    }

    const negativeCount = toNonNegativeNumericValue(
      car?.blindNegativeOfferCount
    )
    if (negativeCount !== null) {
      return negativeCount > 0
    }

    return false
  }

  const getBlindPositiveCounter = (car) => {
    const positiveCount = toNonNegativeNumericValue(
      car?.blindPositiveOfferCount
    )
    if (positiveCount !== null) {
      return positiveCount
    }

    return hasBlindPositiveSignal(car) ? 1 : 0
  }

  const getBlindNegativeCounter = (car) => {
    const negativeCount = toNonNegativeNumericValue(
      car?.blindNegativeOfferCount
    )
    if (negativeCount !== null) {
      return negativeCount
    }

    return hasBlindNegativeSignal(car) ? 1 : 0
  }

  const hasFirstChoice = (car) => !!car?.firstChoice

  const hasSecondChoice = (car) => !!car?.secondChoice

  const hasChoice = (car) =>
    hasFirstChoice(car) || hasSecondChoice(car) || hasBlindPrice(car)

  const computedBlindPositiveOffers = clampRecapCount(
    cars.reduce(
      (previousTotal, car) => previousTotal + getBlindPositiveCounter(car),
      0
    ),
    cars.length || 0
  )
  const computedBlindNegativeOffers = clampRecapCount(
    cars.reduce(
      (previousTotal, car) => previousTotal + getBlindNegativeCounter(car),
      0
    ),
    cars.length || 0
  )
  const totalCarsCount = cars.length || 0
  const explicitBlindPositiveOffersRaw = toNonNegativeNumericValue(
    blindOffersUsedByType?.positive
  )
  const explicitBlindNegativeOffersRaw = toNonNegativeNumericValue(
    blindOffersUsedByType?.negative
  )
  const explicitBlindPositiveCapRaw = toNonNegativeNumericValue(
    blindOffersTotalByType?.positive
  )
  const explicitBlindNegativeCapRaw = toNonNegativeNumericValue(
    blindOffersTotalByType?.negative
  )
  const explicitBlindPositiveOffers =
    explicitBlindPositiveOffersRaw === null
      ? null
      : clampRecapCount(explicitBlindPositiveOffersRaw, totalCarsCount)
  const explicitBlindNegativeOffers =
    explicitBlindNegativeOffersRaw === null
      ? null
      : clampRecapCount(explicitBlindNegativeOffersRaw, totalCarsCount)
  const blindPositiveOffersCount =
    explicitBlindPositiveOffers ?? computedBlindPositiveOffers
  const blindNegativeOffersCount =
    explicitBlindNegativeOffers ?? computedBlindNegativeOffers
  const explicitBlindPositiveCap =
    explicitBlindPositiveCapRaw === null
      ? null
      : clampRecapCount(explicitBlindPositiveCapRaw, totalCarsCount)
  const explicitBlindNegativeCap =
    explicitBlindNegativeCapRaw === null
      ? null
      : clampRecapCount(explicitBlindNegativeCapRaw, totalCarsCount)
  const blindOfferCapsByType = getBlindOfferCapsByType({
    caps,
    defaultPositiveCap: totalCarsCount,
    defaultNegativeCap: totalCarsCount,
  })
  const blindPositiveOffersCap =
    explicitBlindPositiveCap ??
    clampRecapCount(blindOfferCapsByType.positive, totalCarsCount)
  const blindNegativeOffersCap =
    explicitBlindNegativeCap ??
    clampRecapCount(blindOfferCapsByType.negative, totalCarsCount)
  const recapCaps = {
    max: {
      ...caps,
      firstChoice: clampRecapCount(
        caps?.firstChoice ?? totalCarsCount,
        totalCarsCount
      ),
      blindPositiveOffers: blindPositiveOffersCap,
      blindNegativeOffers: blindNegativeOffersCap,
      total: totalCarsCount,
    },
    selected: {
      allChoices: cars.filter((car) => hasChoice(car)).length,
      noChoices: cars.filter((car) => !hasChoice(car)).length,
      firstChoice: cars.filter((car) => hasFirstChoice(car)).length,
      blindPositiveOffers: blindPositiveOffersCount,
      blindNegativeOffers: blindNegativeOffersCount,
      favorites: cars.filter((car) => car.favorite).length,
      total: cars.reduce(
        (previousTotal, car) =>
          ((hasChoice(car) && car.price) || 0) + previousTotal,
        0
      ),
    },
  }

  return (
    <>
      {isDetails && (
        <Typography
          variant="body2"
          color="primaryText"
          sx={{ pb: 1 }}
          data-testid="choices-recap-details-title"
        >
          <strong>{t('carsToBuy.labelTitle.yourSelection')}</strong>
        </Typography>
      )}
      <Stack direction="row" data-testid="choices-recap">
        {!isDetails && (
          <>
            <ChoiceRecap
              id="total"
              title={t('carsToBuy.labelTitle.allCars')}
              value={recapCaps.max.total}
              handleClick={() => setFilteringCaps(FILTER_CAPS_NONE_CHOICE)}
              border
            />
            <Box pr={2} />
            <Divider orientation="vertical" variant="middle" flexItem />
            <Box pr={2} />
          </>
        )}
        <ChoiceRecap
          id={FILTER_CAPS_FIRST_CHOICE}
          title={t('carsToBuy.labelTitle.firstChoice')}
          value={`${recapCaps.selected.firstChoice}/${recapCaps.max.firstChoice}`}
          handleClick={
            !isDetails
              ? () => setFilteringCaps(FILTER_CAPS_FIRST_CHOICE)
              : undefined
          }
          selected={filteringCap === FILTER_CAPS_FIRST_CHOICE}
          border
          sx={{ flex: isDetails ? 1 : undefined }}
        />
        <Box pr={1} />
        {!isDetails && showBlindOffers && (
          <>
            {showBlindPositiveOffers && (
              <>
                <ChoiceRecap
                  id={FILTER_CAPS_BLIND_POSITIVE_OFFERS}
                  title={t('carsToBuy.labelTitle.blindPositiveOffers')}
                  value={`${recapCaps.selected.blindPositiveOffers}/${recapCaps.max.blindPositiveOffers}`}
                  handleClick={() =>
                    setFilteringCaps(FILTER_CAPS_BLIND_POSITIVE_OFFERS)
                  }
                  selected={filteringCap === FILTER_CAPS_BLIND_POSITIVE_OFFERS}
                  border
                />
                <Box pr={1} />
              </>
            )}
            <ChoiceRecap
              id={FILTER_CAPS_BLIND_NEGATIVE_OFFERS}
              title={t('carsToBuy.labelTitle.blindNegativeOffers')}
              value={`${recapCaps.selected.blindNegativeOffers}/${recapCaps.max.blindNegativeOffers}`}
              handleClick={() =>
                setFilteringCaps(FILTER_CAPS_BLIND_NEGATIVE_OFFERS)
              }
              selected={filteringCap === FILTER_CAPS_BLIND_NEGATIVE_OFFERS}
              border
            />
            <Box pr={1} />
          </>
        )}
        {!isDetails && (
          <>
            <ChoiceRecap
              id={FILTER_CAPS_ALL_CHOICES}
              title={t('carsToBuy.labelTitle.allChoices')}
              value={`${recapCaps.selected.allChoices}`}
              handleClick={() => setFilteringCaps(FILTER_CAPS_ALL_CHOICES)}
              selected={filteringCap === FILTER_CAPS_ALL_CHOICES}
              border
            />
            <Box pr={1} />
            <ChoiceRecap
              id={FILTER_CAPS_NO_CHOICES}
              title={t('carsToBuy.labelTitle.noChoices')}
              value={`${recapCaps.selected.noChoices}`}
              handleClick={() => setFilteringCaps(FILTER_CAPS_NO_CHOICES)}
              selected={filteringCap === FILTER_CAPS_NO_CHOICES}
              border
            />
            {onToggleFavorites && (
              <>
                <Box pr={1} />
                <ChoiceRecap
                  id="favorites"
                  title={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <FavoriteIcon
                        sx={{
                          fontSize: 14,
                          color: onlyFavorites ? 'error.main' : 'inherit',
                        }}
                      />
                      <span>{t('carsToBuy.labelTitle.favorites')}</span>
                    </Stack>
                  }
                  value={`${recapCaps.selected.favorites}`}
                  handleClick={onToggleFavorites}
                  selected={onlyFavorites}
                  border
                />
              </>
            )}
            <Box pr={2} />
            <Divider orientation="vertical" variant="middle" flexItem />
            <ChoiceRecap
              title={t('carsToBuy.labelTitle.totalValueSelected')}
              value={`€ ${numberFormatter.format(recapCaps.selected.total)}`}
            />
          </>
        )}
      </Stack>
    </>
  )
}

ChoicesRecap.propTypes = {
  variant: T.oneOf([VARIANT_LIST, VARIANT_DETAILS]),
  cars: T.array,
  caps: T.object,
  blindOffersUsedByType: T.shape({
    positive: T.number,
    negative: T.number,
  }),
  blindOffersTotalByType: T.shape({
    positive: T.number,
    negative: T.number,
  }),
  showBlindOffers: T.bool,
  showBlindPositiveOffers: T.bool,
  filteringCap: T.string,
  setFilteringCaps: T.func,
  onlyFavorites: T.bool,
  onToggleFavorites: T.func,
  readOnly: T.bool,
}
